import { apiUrls } from "../../utils/api";
import {
  addClasses,
  addDeadlineDateToTasks,
  compareWBS,
  departmentSortResources,
  flatTasks,
  getOpenTaskDetailsItem,
  getScrollToTaskContextMenu,
  getRollupContextMenu,
  getViewPresets,
  getWorkingTime,
  getYesNoColumnFilter,
  mergeColumns,
  prepareResources,
  setCursorAtEnd,
  setMinDurationToPreventBeMilestone,
  taskBarTooltip,
  getHeaderContextMenu,
} from "../../utils/ganttUtils";
import { AsyncHelper, DateHelper, Mask, ProjectModel, ResourceHistogram } from "@bryntum/gantt";
import { dateTimeFormatter, histogramTooltipFormat } from "../../utils/format";
import { defaultViewPreset } from "../../utils/constants";
import { translate } from "../../utils/translate";
import dayjs from "dayjs";

export const getGanttConfig = (configs, generalProjectSettings, parent) => {
  const defaultColumns = [
    {
      id: "name",
      order: 10,
      type: "name",
      text: "Name",
      width: 351,
      editor: false,
    },
    {
      id: "startDate",
      type: "startdate",
      field: "startDate",
      text: translate("Start date"),
      width: 140,
      order: 7,
    },
    {
      id: "endDate",
      type: "enddate",
      field: "endDate",
      text: translate("End date"),
      width: 124,
      order: 6,
    },
    {
      id: "effort",
      type: "effort",
      text: translate("Planned hours"),
      filterable: false,
      width: 124,
      order: 5,
    },
    {
      id: "duration",
      type: "duration",
      text: translate("Duration"),
      width: 140,
      order: 11,
    },
    {
      id: "percentDone",
      field: "percentDone",
      type: "percentdone",
      width: 90,
      text: translate("% Done"),
      order: 9,
    },
    {
      id: "rollup",
      order: 8,
      type: "rollup",
      width: 76,
      filterable: getYesNoColumnFilter("rollup"),
    },
    {
      id: "earlyStartDate",
      type: "earlystartdate",
      columnId: "earlystartdate",
      field: "earlyStartDate",
      width: 130,
      text: translate("Early Start"),
      hidden: true,
      editor: false,
    },
    {
      id: "lateEndDate",
      type: "lateenddate",
      field: "lateEndDate",
      columnId: "lateenddate",
      width: 130,
      text: translate("Late End"),
      hidden: true,
      editor: false,
    },
    {
      id: "projectNumber",
      columnId: "projectnumber",
      field: "projectNumber",
      width: 130,
      text: translate("Project number"),   
      hidden: true,
      editor: false,
    },
    {
      id: "CustomerName",
      columnId: "CustomerName",
      field: "CustomerName",
      width: 130,
      text: translate("CustomerName"),
      hidden: false,
      editor: false,
    },
    {
      id: "OrderNumber",
      columnId: "OrderNumber",
      editor: false,
      width: 130,
      field: "OrderNumber",
      text: translate("OrderNumber"),
      hidden: false,
    },
    {
      id: "prefix",
      columnId: "prefix",
      field: "prefix",
      text: translate("Code"),
      order: -1,
      hidden: false,
    },  
    {
      id: "probabilityOfSale",
      columnId: "probabilityOfSale",
      editor: false,
      width: 130,
      field: "probabilityOfSale",
      text: translate("Probability of sale"),
      hidden: false,
    },
    {
      id: "note",
      order: 9,
      width: 160,
      columnId: "note",
      text: translate("Note"),
      editable: true,
      type: "note",
      field: "note",
      filterable: true,
      editor: {
        type: 'textareapickerfield',
        autoSelect: false,
        listeners: {
          focusIn({ source }) {
            source.showPicker();
            setTimeout(() => {
              setCursorAtEnd(source.input);
            });
          },
          trigger: ({ source }) => {
            setTimeout(() => {
              setCursorAtEnd(source.input);
            });
          },
        },
      },
    },
    {
      id: "projectManager",
      editor: false,
      text: translate("projectManager"),
      field: "projectManager",
      columnId: "projectManager",
      width: 130,
      order: -1,
    },
    {
      id: "resourceassignment",
      editor: false,
      type: "resourceassignment",
      columnId: "resourceassignment",
      width: 130,
      order: -1,
    },
  ];
  

  const columns = mergeColumns(configs.columns, defaultColumns);
  const hideWeekends = configs.hideWeekends || false;
  const headerTpl = ({ currentPage, totalPages }) => `
  <div class="demo-export-header">
      <h2>${parent.pageTitle}</h2>  
      <dl>
          <dt>${translate("Date")}: ${dateTimeFormatter(Date.now())}</dt>
          <dd>${totalPages ? `${translate("Page")}: ${currentPage + 1}/${totalPages}` : ""}</dd>
      </dl>
  </div>`;

  return {
    rowHeight: 30,
    barMargin: 3,
    rollupsFeature: {
      rollupToAllParents: true,
    },
    infiniteScroll: true,
    workingTime: getWorkingTime(hideWeekends),
    enableDeleteKey: false,
    weekStartDay: (generalProjectSettings.StartDayOfWeek == undefined || generalProjectSettings.StartDayOfWeek == null) ? 1 : generalProjectSettings.StartDayOfWeek,
    projectLinesFeature: {
      disabled: true,
    },
    taskTooltipFeature: {
      template: taskBarTooltip,
    },
    percentBarFeature: {
      allowResize: false,
    },
    labelsFeature: {
      after: {
        renderer({ taskRecord }) {
          return `${taskRecord.description}`;
        },
      },
    },
    printFeature: {
      headerTpl,
      cls: "exportDialog",
      bodyCls: "exportDialogBody",
      lockPaperSize: true,
      scheduleRange:'daterange',
      exportDialog: {
        autoSelectVisibleColumns: false,
        items: {
          columnsField: { value: ["name", "sequenceNumber"] },
          exporterTypeField: {
            value: "multipagevertical"
          },
        },
      },
    },
    rowReorderFeature: false,
    filterFeature: true,
    nonWorkingTimeFeature: false,
    taskContextMenuFeature: false,
    headerMenuFeature: getHeaderContextMenu(),
    cellEditFeature: {
      addNewAtEnd: false,
      triggerEvent: 'cellclick'
    },
    taskEditFeature: false,
    eventEditFeature: false,
    taskResizeFeature: false,
    taskDragFeature: false,
    taskDragCreateFeature: false,
    timeRangesFeature: {
      showCurrentTimeLine: false,
      showHeaderElements: false,
      enableResizing: true,
    },
    timeRanges: [
      {
        id: 1,
        name: "Today",
        startDate: DateHelper.add(DateHelper.startOf(new Date(), "day"), 1, 'ms'),
      },
    ],
    dependenciesFeature: {
      allowCreate: false,
    },
    taskMenuFeature: {
      items: {
        scrollToTask: getScrollToTaskContextMenu(),
        openDetails: getOpenTaskDetailsItem(),
        rollup: getRollupContextMenu(),
      },
      processItems({ items, taskRecord }) {
        // remove all items except ..
        const taskHasChildren = taskRecord.children?.length > 0;
        Object.keys(items).forEach((item) => {
          if (!["scrollToTask", "openDetails", "rollup"].includes(item)) {
            items[item] = false;
          } else if (item === "rollup" && !taskHasChildren) {
            items[item] = false;
          }
        });

        
      },
    },
    timeAxisHeaderMenuFeature: {
      items: {
        eventsFilter: false,
        dateRange: false,
        currentTimeLine: false,
      },
    },
    indicatorsFeature: {
      items: {
        earlyDates: configs.indicatorEarlyDates || false,
        constraintDate: configs.indicatorConstraintDate || false,
        lateDates: configs.indicatorLateDates || false,
        deadlineDate: false, // configs.indicatorDeadlineDate || false, todo: not working on this page,
        materialDate: function (taskRecord) {
          return taskRecord.materialDate
            ? {
                startDate: new Date(taskRecord.materialDate),
                iconCls: "b-fa b-fa-dolly material-indicator",
                name: "Material date",
              }
            : null;
        },
      },
    },
    columns,
    presets: getViewPresets(),
    viewPreset: configs.viewPreset || defaultViewPreset,
    subGridConfigs: {
      locked: {
        readOnly: true,
        width: configs.taskGridWidth ? configs.taskGridWidth + "%" : "48%",
      },
    },
  };
};

/**
 * @param departments {{[key: string]: boolean}}
 * @returns {{id: string, filterBy: ((function(*): (boolean|*))|*)}}
 */
export const getDepartmentFilter = (departments = {}) => {
  return {
    id: "resources-in-department",
    filterBy: (record) => {
      if (!Object.keys(departments).length) return true;
      return !!departments[record.departmentId];
    },
  };
};

export function getDepartmentFilters(departments) {
  return {
    id: "tasks-department-filter",
    filterBy(record) {
      return departments.includes(record.FK_DEPARTMENT);
    },
  };
}

async function setHistogramData({ histogram, histogramProject, minOfferScoringPercentage }, apiClient) {
  const dataParams = {
    MinOfferScoringPercentage: minOfferScoringPercentage || 0,
    };
  const calendarParams = {
      includeServiceAppointments : true,
  };
  const mask = Mask.mask(
    {
      text: "Loading histogram data",
      mode: "dark",
    },
    histogram.element
  );
  await AsyncHelper.sleep(10);
  const [{ resources, calendars }, { tasks, bryntumAssignments }] = await Promise.all([
    apiClient.get(apiUrls.GetRidderResourcesAndCalendars, calendarParams),
    apiClient.get(apiUrls.HistogramDataByDepartment, dataParams),
  ]).then((resp) => Promise.all(resp.map((i) => i.json())));
  await AsyncHelper.sleep(10);
  mask.text = "Calculating histogram data";
  await histogramProject.loadInlineData({
    eventsData: tasks,
    resourcesData: prepareResources(resources),
    assignmentsData: bryntumAssignments,
  });
  mask.text = "Loading histogram calendar";

  await histogramProject.calendarManagerStore.loadDataAsync(calendars);
  //  histogram.collapseAll();
  Mask.unmask(histogram.element);
}

export const createHistogram = async ({ gantt, target, minOfferScoringPercentage, departmentsFilter, readOnly }, apiClient) => {
  await AsyncHelper.sleep(10);

  const histogramProject = new ProjectModel({
    calendar: "DefaultCompanyCalendar",
    readOnly: readOnly,
    hoursPerDay: 24,
    daysPerWeek: 5,
    daysPerMonth: 20,
    calendarsData: [],
    resourcesData: [],
    resourceStore: {
      filters: [getDepartmentFilter(departmentsFilter)],
      sorters: [
        { field: 'sortOrder', ascending: true },
        { field: 'name', ascending: true },
      ],
    },
    eventsData: [],
    assignmentsData: [],
  });
  const histogram = new ResourceHistogram({
    appendTo: target,
    project: histogramProject,
    //startDate: gantt.startDate,
    //endDate: gantt.endDate,
    readOnly: readOnly,
    height: "100%",
    minHeight: "20em",
    showBarTip: true,
    rowHeight: 30,
    barMargin: 3,
    showBarText: true,
    features: {
      timeAxisHeaderMenu: {
        items: {
          eventsFilter: false,
          dateRange: false,
          currentTimeLine: false,
        },
      },
      group: {
        field: "departmentdescription",
        groupSortFn: departmentSortResources,
      },
      cellMenu: {
        items: {
          removeRow: false,
        },
      },
    },
    getBarText(datum) {
      if (datum.effort) {
        const unit = "hour";
        const allocated = DateHelper.as(unit, datum.effort);
        const available = DateHelper.as(unit, datum.maxEffort);
        return `${Math.round((allocated * 100) / available)}% `;
      }
    },
    columns: [
      {
        width: 200,
        field: "name",
        text: "Resource",
      },
      {
        width: 200,
        field: "events.length",
        text: "events",
      },
    ],
  });

  histogram._effortFormatter = histogramTooltipFormat;
  const preserveVisibleStart =
    gantt?.currentOrientation?.visibleDateRange?.startDate != null
      ? new Date(gantt.currentOrientation.visibleDateRange.startDate.getTime())
      : null;
  histogram.addPartner(gantt);
  // Partner + infiniteScroll realigns the gantt time axis; shiftToDate reapplies the correct buffered span.
  if (preserveVisibleStart && typeof gantt.shiftToDate === "function") {
    gantt.shiftToDate(preserveVisibleStart, null);
    if (typeof gantt.nextAnimationFrame === "function") {
      await gantt.nextAnimationFrame();
    }
  }

  await setHistogramData({
    histogram,
    histogramProject,
    minOfferScoringPercentage,
  }, apiClient);
  window.histogram = histogram;
  return histogram;
};

/**
 * @param params
 * @returns {Promise<Partial<ProjectModelConfig>>}
 */
export const generateDataset = async (params, apiClient) => {
  const calendarParams = {
    includeServiceAppointments: true,
  };
  const stream = await apiClient.post(apiUrls.TotalProjectsOverview, params);
  const data = await stream.json();
  const stream2 = await apiClient.get(apiUrls.GetRidderResourcesAndCalendars, calendarParams);
  const { resources, calendars } = await stream2.json();

  const tasksList = flatTasks(data.tasks);
  tasksList.forEach((task) => {
    if (task.constraintDate) {
      task.constraintDate = new Date(task.constraintDate);
    }
  });
  setMinDurationToPreventBeMilestone(tasksList);
  addDeadlineDateToTasks(tasksList);
  addClasses(tasksList, true);
  tasksList.forEach((task) => {
    task.endDate = task.endDate ? new Date(task.endDate) : task.endDate;
    task.startDate = task.startDate ? new Date(task.startDate) : task.startDate;
  });
  function fixNumber(num) {
    let wbsParts = num.split(".") || [];
    wbsParts = wbsParts.map((x) => x.padStart(5, "0"));
    return wbsParts.join("");
  }
  function wbsSorter(recordA, recordB) {
    const wbs1 = fixNumber(recordA?.ERPWbs || "0");
    const wbs2 = fixNumber(recordB?.ERPWbs || "0");
    // sort by date if wbs is equal
    if (wbs1 === wbs2) {
      const start1 = dayjs(recordA.startDate).valueOf();
      const start2 = dayjs(recordB.startDate).valueOf();
      if (start1 === start2) return 0;
      return start1 < start2 ? -1 : 1;
    }
    return 0;
  }

  return {
    enableProgressNotifications: true,
    project: {
      effectiveDirection: {
        kind: "enforced",
      },
    },
    taskStore: {
      useRawData: true,
      reapplyFilterOnAdd: true,
      reapplyFilterOnUpdate: false,
      sorters: [
        { field: "ERPWbs", ascending: true },
        { field: "segmentSeqNr", ascending: true }
      ],
      listeners: {
        beforeSort: (e) => {
          if (!e.sorters?.length) {
            setTimeout(() => {
              e.source.addSorter(wbsSorter);
            }, 100);
          }
        },
      },
      fields: [
        {
          name: "ERPWbs",
          compare: compareWBS,
        },
        { name: "projectColor", persist: false }, // exclude from save
        { name: "cls", persist: false }, // exclude from save

        { name: "RidderType", type: 'string' },
        { name: "OrderNumber", type: 'string' },
        { name: "PlanLevel", type: 'string' },
        { name: "DepartmentDescription", type: 'string' },
        { name: "WorkActivityDescription", type: 'string' },
        { name: "WorkActivityGroupDescription", type: 'string' },
        { name: "projectNumber", type: 'string' }, 
        { name: "probabilityOfSale", type: 'number' }, 
/*        { type: 'duration' }, */
        { name: "assignments", type: 'string' },
        { name: "projectManager", type: 'string' },
        { name: "CustomerName", type: 'string' },
        { name: "prefix", type: 'string' }, 
        { name: "segmentSeqNr", type: 'number' }, 
      ],
    },
    assignmentStore: { useRawData: true },
    dependencyStore: { useRawData: true },
    tasksData: data.tasks,
    resourcesData: resources,
    resourceStore: {
      useRawData: true,
    },
    calendarsData: calendars,
    calendarManagerStore: { useRawData: true },
    dependenciesData: data.bryntumDependencies,
    assignmentsData: data.bryntumAssignments,
  };
};
